import { createBrowserRouter } from 'react-router';

import { AppLayout, NotFound, PublicLayout } from './components/Layouts.js';
import { Account } from './pages/Account.js';
import { Area } from './pages/Area.js';
import { Changes } from './pages/Changes.js';
import { Modernization } from './pages/Modernization.js';
import { ModernizationCompare } from './pages/ModernizationCompare.js';
import { AiSearch } from './pages/AiSearch.js';
import { Report } from './pages/Report.js';
import { ModernizationPackage } from './pages/ModernizationPackage.js';
import { Billing } from './pages/Billing.js';
import { AddSite } from './pages/AddSite.js';
import {
  ConfirmRecipient,
  ForgotPassword,
  Login,
  Register,
  ResetPassword,
  VerifyEmail,
} from './pages/Auth.js';
import { SiteLayout } from './components/site.js';
import { Findings } from './pages/Findings.js';
import { HelpRequest } from './pages/HelpRequest.js';
import { Incidents } from './pages/Incidents.js';
import { Links } from './pages/Links.js';
import { Notifications } from './pages/Notifications.js';
import { Onboarding } from './pages/Onboarding.js';
import { Overview } from './pages/Overview.js';
import { AccountDeleted, ExportDownload, RestoreAccount } from './pages/Privacy.js';
import { QuickCheckConfirm, QuickCheckResult, QuickCheckStart } from './pages/QuickCheck.js';
import { Requests } from './pages/Requests.js';
import { SiteDashboard } from './pages/SiteDashboard.js';
import { SiteSettings } from './pages/SiteSettings.js';
import { Speed } from './pages/Speed.js';

export const routes = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/verify-email', element: <VerifyEmail /> },
      { path: '/confirm-recipient', element: <ConfirmRecipient /> },
      { path: '/restore-account', element: <RestoreAccount /> },
      { path: '/account-deleted', element: <AccountDeleted /> },
    ],
  },
  // Public Quick-Check (US-09): no account needed.
  {
    element: <PublicLayout wide />,
    children: [
      { path: '/check', element: <QuickCheckStart /> },
      { path: '/check/:checkId', element: <QuickCheckResult /> },
      { path: '/check/:checkId/confirm', element: <QuickCheckConfirm /> },
    ],
  },
  {
    element: <AppLayout />,
    children: [
      { path: '/', element: <Overview /> },
      { path: '/sites/new', element: <AddSite /> },
      {
        path: '/sites/:siteId',
        element: <SiteLayout />,
        children: [
          { index: true, element: <SiteDashboard /> },
          { path: 'onboarding', element: <Onboarding /> },
          { path: 'area/:area', element: <Area /> },
          { path: 'changes', element: <Changes /> },
          { path: 'modernization', element: <Modernization /> },
          { path: 'modernization/compare', element: <ModernizationCompare /> },
          { path: 'modernization/:packageKey', element: <ModernizationPackage /> },
          { path: 'findings', element: <Findings /> },
          { path: 'incidents', element: <Incidents /> },
          { path: 'links', element: <Links /> },
          { path: 'speed', element: <Speed /> },
          { path: 'ai-search', element: <AiSearch /> },
          { path: 'report', element: <Report /> },
          { path: 'settings', element: <SiteSettings /> },
          { path: 'help', element: <HelpRequest /> },
        ],
      },
      { path: '/requests', element: <Requests /> },
      { path: '/billing', element: <Billing /> },
      // The operator area is loaded on demand: customers never download it.
      {
        path: '/admin',
        lazy: async () => ({
          Component: (await import('./pages/admin/AdminLayout.js')).AdminLayout,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminAccounts.js')).AdminAccounts,
            }),
          },
          {
            path: 'accounts/:accountId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminAccounts.js')).AdminAccount,
            }),
          },
          {
            path: 'requests',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminRequests.js')).AdminRequests,
            }),
          },
          {
            path: 'requests/:requestId',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminRequests.js')).AdminRequest,
            }),
          },
          {
            path: 'services',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminServices.js')).AdminServices,
            }),
          },
          {
            path: 'system',
            lazy: async () => ({
              Component: (await import('./pages/admin/AdminSystem.js')).AdminSystem,
            }),
          },
        ],
      },
      { path: '/notifications', element: <Notifications /> },
      { path: '/account', element: <Account /> },
      { path: '/export', element: <ExportDownload /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes);
